// Creator media (avatar + portfolio image) upload constraints, shared by the
// upload UI and its tests (PIT-71).
//
// These mirror the `creator-media` bucket's own limits in
// supabase/migrations/20260811153203_creator_media.sql. The bucket rejects an
// oversized or wrong-typed object regardless — checking here just turns that
// rejection into a sentence the creator can act on, before the upload starts.

export const MAX_IMAGE_BYTES = 10 * 1024 * 1024; // 10MB
export const MAX_IMAGE_LABEL = '10MB';

/** Image types the bucket accepts. Keep in step with the migration. */
export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'] as const;

/** `accept` attribute for the file inputs. */
export const ACCEPTED_IMAGE_ATTR = ACCEPTED_IMAGE_TYPES.join(',');

/** How many portfolio images a creator may hold. */
export const MAX_PORTFOLIO_IMAGES = 6;

/**
 * Result of validating a selected image. A discriminated union on `ok` so
 * callers narrow to `error` only on the failure branch — same shape as
 * {@link ../lib/campaignBrief.validateBrief}.
 */
export type ImageValidation = { ok: true } | { ok: false; error: string };

/** Validate a user-selected image before it is accepted for upload. */
export function validateImage(file: File | null | undefined): ImageValidation {
  if (!file) return { ok: false, error: 'No image selected' };

  if (!(ACCEPTED_IMAGE_TYPES as readonly string[]).includes(file.type)) {
    return { ok: false, error: 'Images must be JPEG, PNG, WebP or GIF' };
  }

  if (file.size > MAX_IMAGE_BYTES) {
    return { ok: false, error: `Images must be ${MAX_IMAGE_LABEL} or smaller` };
  }

  return { ok: true };
}

/** File extension for a validated image, used to build its storage key. */
export function imageExtension(file: File): string {
  const fromType = file.type.split('/')[1];
  // `image/jpeg` is the MIME for both .jpg and .jpeg; either is fine as a key.
  return fromType === 'svg+xml' ? 'svg' : (fromType ?? 'bin');
}

/**
 * Storage key for a creator's media, under the folder the write policy scopes
 * to their uid. `seed` makes the key unique so replacing an avatar writes a new
 * object rather than relying on cache invalidation for a fixed name.
 */
export function mediaPath(
  userId: string,
  kind: 'avatar' | 'portfolio',
  extension: string,
  seed: string
): string {
  return kind === 'avatar'
    ? `${userId}/avatar-${seed}.${extension}`
    : `${userId}/portfolio/${seed}.${extension}`;
}
